/**
 * ============================================================================
 *  File        : frontend/src/api/auction.ts
 *  Module      : api
 *  Purpose     : 入札協作 API — 開標/締切 + bidder 登録/列表 + 下見枠/予約 +
 *                逐品入札 + ranking + 逐品落札確定/列表（v1.3-05 bid collaboration）
 *  Author      : EF Engineering
 *  Created     : 2026-05-31
 *  Updated     : 2026-05-31
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 *
 *  後端 handler：crates/api-http v13_bid_collaboration（全部 keyed 到 v13
 *  market_cases 的 case_id，UI 建立的案件可直接走完整 lifecycle）。
 *  - POST /api/v1/cases/:case_id/auctions            → 開標（OpenRoundDto）
 *  - POST /api/v1/cases/:case_id/auction/rounds/close → 締切
 *  - POST /api/v1/cases/:case_id/auction/bidders      → bidder 登録
 *  - GET  /api/v1/cases/:case_id/auction/bidders      → bidder 列表
 *  - POST /api/v1/cases/:case_id/auction/preview-slots → 下見枠作成
 *  - GET  /api/v1/cases/:case_id/auction/preview-slots → 下見枠列表
 *  - POST /api/v1/preview-slots/:slot_id/reservations  → 下見予約（呼叫者 org）
 *  - POST /api/v1/cases/:case_id/auction/bids          → 逐品入札（Idempotency-Key）
 *  - GET  /api/v1/cases/:case_id/items/:item_id/auction/ranking → ranking
 *  - POST /api/v1/cases/:case_id/auction/awards        → 逐品落札確定
 *  - GET  /api/v1/cases/:case_id/auction/awards        → 落札列表
 *  型別取自 generated schema.d.ts（已對齊執行中 v13 binary）；
 *  金額・単価一律字串型 Decimal，前端不得轉 float（CLAUDE.md §2）。
 */

import { API_BASE, mutationHeaders, requestData, type ApiSchemas } from './client'

// ---- 型別（取自 generated OpenAPI schema）--------------------------------

/** 開標 request body：opens_at / cutoff_at（RFC3339）。 */
export type OpenRoundDto = ApiSchemas['OpenRoundDto']
/** 入札ラウンド（開標窓口）輸出 DTO。 */
export type BiddingRoundDto = ApiSchemas['BiddingRoundDto']
/** bidder 登録 request body：受邀入札参加者組織 id。 */
export type RegisterBidderDto = ApiSchemas['RegisterBidderDto']
/** 受邀 bidder 輸出 DTO（未 approve 不可入札/予約）。 */
export type BidderDto = ApiSchemas['BidderDto']
/** 下見枠作成 request body：時間窓口 + 容量（預設 1）。 */
export type CreatePreviewSlotDto = ApiSchemas['CreatePreviewSlotDto']
/** 下見枠輸出 DTO（含容量・狀態）。 */
export type PreviewSlotDto = ApiSchemas['PreviewSlotDto']
/** 下見予約輸出 DTO。 */
export type PreviewBookingDto = ApiSchemas['PreviewBookingDto']
/** 入札提交 request body：品項 id + 単価（字串型 Decimal）。 */
export type SubmitBidDto = ApiSchemas['SubmitBidDto']
/** 入札輸出 DTO（ranking row 共用）。 */
export type BidEntryDto = ApiSchemas['BidEntryDto']
/** 落札確定 request body：品項 id + 落札する入札 id。 */
export type ConfirmAwardDto = ApiSchemas['ConfirmAwardDto']
/** 落札輸出 DTO（auction_unit_price = 落札単価，餵 P2 三方分配）。 */
export type AwardDto = ApiSchemas['AwardDto']

const CASES_PATH = `${API_BASE}/cases`

// ---- 1. 開標 / 締切 -------------------------------------------------------

/** 開標：開設入札ラウンド（opens_at/cutoff_at），回傳建立後的 round。 */
export function openAuctionRound(
  caseId: number | string,
  body: OpenRoundDto,
): Promise<BiddingRoundDto> {
  return requestData<BiddingRoundDto>(`${CASES_PATH}/${caseId}/auctions`, {
    method: 'POST',
    body,
    headers: mutationHeaders(),
  })
}

/** 締切：關閉目前 open 的入札ラウンド。 */
export function closeAuctionRound(caseId: number | string): Promise<BiddingRoundDto> {
  return requestData<BiddingRoundDto>(`${CASES_PATH}/${caseId}/auction/rounds/close`, {
    method: 'POST',
    body: {},
    headers: mutationHeaders(),
  })
}

// ---- 2. bidder 登録 / 列表 ------------------------------------------------

/** bidder 登録：邀請入札参加者組織。 */
export function registerBidder(
  caseId: number | string,
  body: RegisterBidderDto,
): Promise<BidderDto> {
  return requestData<BidderDto>(`${CASES_PATH}/${caseId}/auction/bidders`, {
    method: 'POST',
    body,
    headers: mutationHeaders(),
  })
}

/** 列出案件受邀 bidder。 */
export function listBidders(caseId: number | string): Promise<BidderDto[]> {
  return requestData<BidderDto[]>(`${CASES_PATH}/${caseId}/auction/bidders`, { method: 'GET' })
}

// ---- 3. 下見枠 / 予約 -----------------------------------------------------

/** 下見枠作成：時間窓口 + 容量（預設 1，後端拒絕重複預約超過容量）。 */
export function createPreviewSlot(
  caseId: number | string,
  body: CreatePreviewSlotDto,
): Promise<PreviewSlotDto> {
  return requestData<PreviewSlotDto>(`${CASES_PATH}/${caseId}/auction/preview-slots`, {
    method: 'POST',
    body,
    headers: mutationHeaders(),
  })
}

/** 列出案件下見枠。 */
export function listPreviewSlots(caseId: number | string): Promise<PreviewSlotDto[]> {
  return requestData<PreviewSlotDto[]>(`${CASES_PATH}/${caseId}/auction/preview-slots`, {
    method: 'GET',
  })
}

/** 下見予約：以呼叫者自己的 org 預約該枠（body 無欄位）。 */
export function bookPreviewSlot(slotId: number | string): Promise<PreviewBookingDto> {
  return requestData<PreviewBookingDto>(`${API_BASE}/preview-slots/${slotId}/reservations`, {
    method: 'POST',
    body: {},
    headers: mutationHeaders(),
  })
}

// ---- 4. 入札提交 ----------------------------------------------------------

/** 逐品入札提交：締切後後端拒絕（BiddingClosed）。 */
export function submitBid(
  caseId: number | string,
  body: SubmitBidDto,
): Promise<BidEntryDto> {
  return requestData<BidEntryDto>(`${CASES_PATH}/${caseId}/auction/bids`, {
    method: 'POST',
    body,
    headers: mutationHeaders(),
  })
}

// ---- 5. ranking -----------------------------------------------------------

/**
 * 逐品 ranking：単価 DESC → 同価先提出者優先（submitted_at ASC）→ bidder_org_id ASC。
 * 後端 order：UnitPrice DESC, SubmittedAt ASC, BidderOrgId ASC。
 */
export function getRanking(
  caseId: number | string,
  itemId: number | string,
): Promise<BidEntryDto[]> {
  return requestData<BidEntryDto[]>(
    `${CASES_PATH}/${caseId}/items/${itemId}/auction/ranking`,
    { method: 'GET' },
  )
}

// ---- 6. 落札確定 / 列表 ---------------------------------------------------

/**
 * 逐品落札確定：選 awardee（winning_bid_id），回傳含落札単価的 award。
 * 高風險端點（spec §9.2）:需帶 2FA step-up `stepUpCode`（→ `X-2FA-Code` header）。
 */
export function confirmAward(
  caseId: number | string,
  body: ConfirmAwardDto,
  stepUpCode?: string,
): Promise<AwardDto> {
  return requestData<AwardDto>(`${CASES_PATH}/${caseId}/auction/awards`, {
    method: 'POST',
    body,
    headers: mutationHeaders(stepUpCode),
  })
}

/** 列出案件已確定落札。 */
export function listAwards(caseId: number | string): Promise<AwardDto[]> {
  return requestData<AwardDto[]>(`${CASES_PATH}/${caseId}/auction/awards`, { method: 'GET' })
}
